import { IRect } from '@pressreader/geometry';
import { TargetPlatform } from '@pressreader/types';
import { IScreenInfo, ScreenOrientation } from '@pressreader/ui-types';
import { newGuid } from '@pressreader/utils';

import { getScreenSizeType } from './screen.size';

function computeScreenInfo() {
    const winWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const winHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

    if (winWidth === undefined || winHeight === undefined) {
        throw new Error('Critical error: document size is undefined.');
    }

    const orientation = winWidth > winHeight ? ScreenOrientation.Landscape : ScreenOrientation.Portrait;
    const newContextValue: IScreenInfo = {
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        screenType: getScreenSizeType(),

        availWidth: winWidth,
        availHeight: winHeight,

        orientation,
        devicePixelRatio: window.devicePixelRatio,

        headerHeight: 0,

        viewport: { top: 0, left: 0, width: winWidth, height: winHeight },
    };

    return newContextValue;
}

function getViewportHashCode(viewport: IRect, fontId: string | null, fontScale: number): string {
    return `vp_${viewport.width}_${fontId || ''}_${fontScale}`;
}

function getPlatformClassName(platform: TargetPlatform) {
    if (platform === TargetPlatform.Voyager) {
        // use styles for the web platform
        return `platform-${TargetPlatform.Web} platform-extend-${TargetPlatform.Voyager}`;
    }
    return `platform-${platform}`;
}

/**
 * @deprecated
 * Please use useId() hook.
 */
function newHtmlId() {
    // html id should start from a-z characters
    return `a${newGuid()}`;
}

function outerHeight(element: HTMLElement) {
    let height = element.offsetHeight;
    const style = getComputedStyle(element);

    height += parseInt(style.marginTop, 10) + parseInt(style.marginBottom, 10);
    return height;
}

function isInViewport(element: HTMLElement, offset: { x?: number; y?: number }) {
    const bounding = element.getBoundingClientRect();

    const offsetX = offset.x || 0;
    const offsetY = offset.y || 0;

    return (
        bounding.top + offsetY >= 0 &&
        bounding.left + offsetX >= 0 &&
        bounding.bottom - offsetY <= (window.innerHeight || document.documentElement.clientHeight) &&
        bounding.right - offsetX <= (window.innerWidth || document.documentElement.clientWidth)
    );
}

function findAncestor(element: Element, selector: string) {
    if (typeof element.closest === 'function') {
        return element.closest(selector) || null;
    }

    if (!Element.prototype.matches) {
        // seems like it should be supported by most browsers and there is a prolyfill also in place
        // just in case it runs on some old browsers and polifill didn't work
        return null;
    }

    for (let el: Element | null = element; el; el = el.parentElement) {
        if (el.matches(selector)) {
            return el;
        }
    }

    return null;
}

/**
 * Sorts the list of elements in the order they appear in the DOM.
 * Order:
 * - Earlier in document comes first
 * - Parent before child
 * - Left-to-right within same level
 *
 * @param elements - a list of HTML elements to sort.
 * @returns - a sorted list of elements.
 */
const sortElementsInDOMOrderAsc = (items: HTMLElement[]): typeof items => {
    return [...items].sort((a, b) => {
        const position = a.compareDocumentPosition(b);
        return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });
};

export {
    computeScreenInfo,
    findAncestor,
    getPlatformClassName,
    getViewportHashCode,
    isInViewport,
    newHtmlId,
    outerHeight,
    sortElementsInDOMOrderAsc,
};
